/**
 * DeploymentsModal Component
 *
 * Modal for viewing all deployments of an application with detailed information.
 * Supports version tagging, custom tags, activation, and rollback actions.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { deploymentService } from '../../../api/deployment.service';
import type { ApplicationSanitized } from '../../../types/application.types';
import type { DeploymentSanitized } from '../../../types/deployment.types';
import { getErrorMessage } from '../../../lib/utils/errors';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { SetVersionTagModal } from './SetVersionTagModal';
import { AddCustomTagsModal } from './AddCustomTagsModal';
import { RollbackModal } from './RollbackModal';
import { ActivateDeploymentModal } from './ActivateDeploymentModal';

interface DeploymentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  application: ApplicationSanitized;
}

/**
 * Get badge color based on deployment status
 */
const getStatusColor = (status: string) => {
  switch (status) {
    case 'deployed':
      return 'bg-green-100 text-green-800';
    case 'deploying':
      return 'bg-blue-100 text-blue-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'rolled_back':
      return 'bg-yellow-100 text-yellow-800';
    case 'pending':
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Format deployment status for display
 */
const formatStatus = (status: string) => {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * DeploymentsModal component
 */
export const DeploymentsModal: React.FC<DeploymentsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  application,
}) => {
  const { activeRole } = useAuth();

  // Permission checks
  const canManageDeployments = hasPermission(activeRole?.permissions, 'applications:deployment:update');

  const [selectedDeployment, setSelectedDeployment] = useState<DeploymentSanitized | null>(null);

  // Nested modal states
  const [showSetVersionTagModal, setShowSetVersionTagModal] = useState(false);
  const [showAddCustomTagsModal, setShowAddCustomTagsModal] = useState(false);
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [deploymentForAction, setDeploymentForAction] = useState<DeploymentSanitized | null>(null);

  // Fetch deployments
  const {
    data: deployments = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['deployments', tenantId, application.applicationId],
    queryFn: () => deploymentService.listDeployments(tenantId, application.applicationId),
    enabled: isOpen,
  });

  const handleClose = () => {
    setSelectedDeployment(null);
    setDeploymentForAction(null);
    setShowSetVersionTagModal(false);
    setShowAddCustomTagsModal(false);
    setShowRollbackModal(false);
    setShowActivateModal(false);
    onClose();
  };

  // Action handlers
  const handleSetVersionTag = (deployment: DeploymentSanitized) => {
    setDeploymentForAction(deployment);
    setShowSetVersionTagModal(true);
  };

  const handleAddCustomTags = (deployment: DeploymentSanitized) => {
    setDeploymentForAction(deployment);
    setShowAddCustomTagsModal(true);
  };

  const handleRollback = (deployment: DeploymentSanitized) => {
    setDeploymentForAction(deployment);
    setShowRollbackModal(true);
  };

  const handleActivate = (deployment: DeploymentSanitized) => {
    setDeploymentForAction(deployment);
    setShowActivateModal(true);
  };

  const handleActionSuccess = () => {
    refetch();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Header>
        Deployments - {application.applicationName}
      </Modal.Header>

      <Modal.Body>
        {/* Error Alert */}
        {error && (
          <Alert variant="error" className="mb-4">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-600">Loading deployments...</span>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && deployments.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No deployments found for this application.</p>
            <p className="text-sm text-gray-400 mt-2">
              Deployments will appear here once you deploy this application.
            </p>
          </div>
        )}

        {/* Deployments List */}
        {!isLoading && !error && deployments.length > 0 && (
          <div className="space-y-4">
            {deployments.map((deployment) => {
              const isActive = deployment.deploymentId === application.currentDeploymentId;
              const isExpanded = selectedDeployment?.deploymentId === deployment.deploymentId;

              return (
                <div
                  key={deployment.deploymentId}
                  className={clsx(
                    'border rounded-lg p-4 transition-all',
                    isActive ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white',
                    'hover:shadow-md cursor-pointer'
                  )}
                  onClick={() =>
                    setSelectedDeployment(isExpanded ? null : deployment)
                  }
                >
                  {/* Header Row */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-gray-900">
                          Version {deployment.version}
                        </h4>
                        {/* Version Tag (purple) */}
                        {deployment.versionTag && (
                          <Badge variant="info" size="sm">
                            {deployment.versionTag}
                          </Badge>
                        )}
                        {/* Legacy tag */}
                        {deployment.tag && !deployment.versionTag && (
                          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                            {deployment.tag}
                          </span>
                        )}
                        {/* Custom Tags (gray) */}
                        {deployment.customTags?.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="neutral" size="sm">
                            {tag}
                          </Badge>
                        ))}
                        {deployment.customTags && deployment.customTags.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{deployment.customTags.length - 3} more
                          </span>
                        )}
                        {/* LATEST Badge (blue/warning) */}
                        {deployment.isLatest && (
                          <Badge variant="warning" size="sm">
                            LATEST
                          </Badge>
                        )}
                        {/* ACTIVE Badge (primary) */}
                        {isActive && (
                          <span className="text-xs bg-primary-600 text-white px-2 py-0.5 rounded font-medium">
                            ACTIVE
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Deployed {formatDistanceToNow(new Date(deployment.createdAt), { addSuffix: true })}
                        {' via '}{deployment.deployedVia}
                      </p>
                    </div>
                    <span
                      className={clsx(
                        'px-3 py-1 rounded-full text-xs font-medium',
                        getStatusColor(deployment.deploymentStatus)
                      )}
                    >
                      {formatStatus(deployment.deploymentStatus)}
                    </span>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                      {/* Deployment Info */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Deployment ID:</span>
                          <div className="mt-1">
                            <CopyableCode value={deployment.deploymentId} />
                          </div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Deployed By:</span>
                          <p className="text-gray-600 mt-1">{deployment.deployedBy}</p>
                        </div>
                      </div>

                      {/* Function ARN */}
                      {deployment.functionArn && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Function ARN:</span>
                          <p className="text-gray-600 font-mono text-xs mt-1 break-all">
                            {deployment.functionArn}
                          </p>
                        </div>
                      )}

                      {/* API Endpoint */}
                      {deployment.apiEndpoint && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">API Endpoint:</span>
                          <p className="text-gray-600 font-mono text-xs mt-1 break-all">
                            <a
                              href={deployment.apiEndpoint}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {deployment.apiEndpoint}
                            </a>
                          </p>
                        </div>
                      )}

                      {/* Code Bundle */}
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Code Bundle:</span>
                        <p className="text-gray-600 font-mono text-xs mt-1 break-all">
                          {deployment.codeBundleS3Path}
                        </p>
                        <p className="text-gray-500 text-xs mt-1">
                          Hash: {deployment.codeBundleHash.substring(0, 16)}...
                        </p>
                      </div>

                      {/* Environment Variables */}
                      {deployment.environmentVariables && Object.keys(deployment.environmentVariables).length > 0 && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Environment Variables:</span>
                          <div className="mt-1 bg-gray-50 rounded p-2 max-h-32 overflow-y-auto">
                            {Object.entries(deployment.environmentVariables).map(([key, value]) => (
                              <div key={key} className="text-xs font-mono text-gray-600">
                                <span className="font-semibold">{key}:</span> {value}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Deployment Duration */}
                      {deployment.deploymentDuration && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Duration:</span>
                          <p className="text-gray-600 mt-1">
                            {(deployment.deploymentDuration / 1000).toFixed(2)}s
                          </p>
                        </div>
                      )}

                      {/* Error Message */}
                      {deployment.errorMessage && (
                        <div className="text-sm">
                          <span className="font-medium text-red-700">Error:</span>
                          <p className="text-red-600 mt-1 bg-red-50 rounded p-2">
                            {deployment.errorMessage}
                          </p>
                        </div>
                      )}

                      {/* Rollback Info */}
                      {deployment.isRolledBack && (
                        <div className="text-sm bg-yellow-50 rounded p-3 border border-yellow-200">
                          <span className="font-medium text-yellow-800">Rolled Back</span>
                          <p className="text-yellow-700 text-xs mt-1">
                            {deployment.rolledBackAt && `On ${new Date(deployment.rolledBackAt).toLocaleString()}`}
                          </p>
                          {deployment.rolledBackReason && (
                            <p className="text-yellow-700 text-xs mt-1">
                              Reason: {deployment.rolledBackReason}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Timestamps */}
                      <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                        <div>
                          <span className="font-medium">Created:</span>
                          <p className="mt-1">{new Date(deployment.createdAt).toLocaleString()}</p>
                        </div>
                        {deployment.deploymentCompletedAt && (
                          <div>
                            <span className="font-medium">Completed:</span>
                            <p className="mt-1">{new Date(deployment.deploymentCompletedAt).toLocaleString()}</p>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons (Admin only) */}
                      {canManageDeployments && (
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex flex-wrap gap-2">
                            {/* Set Version Tag Button */}
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetVersionTag(deployment);
                              }}
                              disabled={!!deployment.versionTag}
                              title={deployment.versionTag ? 'Version tag already set' : 'Set version tag'}
                            >
                              {deployment.versionTag ? 'Tagged' : 'Set Version Tag'}
                            </Button>

                            {/* Add Tags Button */}
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddCustomTags(deployment);
                              }}
                              disabled={(deployment.customTags?.length ?? 0) >= 10}
                              title={(deployment.customTags?.length ?? 0) >= 10 ? 'Maximum tags reached' : 'Add custom tags'}
                            >
                              Add Tags ({deployment.customTags?.length ?? 0}/10)
                            </Button>

                            {/* Activate Button */}
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleActivate(deployment);
                              }}
                              disabled={isActive || deployment.deploymentStatus !== 'deployed'}
                              title={
                                isActive
                                  ? 'Already active'
                                  : deployment.deploymentStatus !== 'deployed'
                                  ? 'Only deployed deployments can be activated'
                                  : 'Activate this deployment'
                              }
                            >
                              {isActive ? 'Active' : 'Activate'}
                            </Button>

                            {/* Rollback Button */}
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRollback(deployment);
                              }}
                              disabled={isActive || deployment.isRolledBack}
                              title={
                                isActive
                                  ? 'Cannot rollback active deployment'
                                  : deployment.isRolledBack
                                  ? 'Already rolled back'
                                  : 'Rollback this deployment'
                              }
                            >
                              {deployment.isRolledBack ? 'Rolled Back' : 'Rollback'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>

      {/* Nested Modals */}
      {showSetVersionTagModal && deploymentForAction && (
        <SetVersionTagModal
          isOpen={showSetVersionTagModal}
          onClose={() => {
            setShowSetVersionTagModal(false);
            setDeploymentForAction(null);
          }}
          tenantId={tenantId}
          applicationId={application.applicationId}
          deployment={deploymentForAction}
          onSuccess={handleActionSuccess}
        />
      )}

      {showAddCustomTagsModal && deploymentForAction && (
        <AddCustomTagsModal
          isOpen={showAddCustomTagsModal}
          onClose={() => {
            setShowAddCustomTagsModal(false);
            setDeploymentForAction(null);
          }}
          tenantId={tenantId}
          applicationId={application.applicationId}
          deployment={deploymentForAction}
          onSuccess={handleActionSuccess}
        />
      )}

      {showRollbackModal && deploymentForAction && (
        <RollbackModal
          isOpen={showRollbackModal}
          onClose={() => {
            setShowRollbackModal(false);
            setDeploymentForAction(null);
          }}
          tenantId={tenantId}
          applicationId={application.applicationId}
          deployment={deploymentForAction}
          onSuccess={handleActionSuccess}
        />
      )}

      {showActivateModal && deploymentForAction && (
        <ActivateDeploymentModal
          isOpen={showActivateModal}
          onClose={() => {
            setShowActivateModal(false);
            setDeploymentForAction(null);
          }}
          tenantId={tenantId}
          applicationId={application.applicationId}
          deployment={deploymentForAction}
          currentActiveDeploymentId={application.currentDeploymentId}
          onSuccess={handleActionSuccess}
        />
      )}
    </Modal>
  );
};
